import { useLayoutEffect, useRef } from "react";

// Tables are wider than a phone. On desktop this scrolls the table sideways
// inside its card. At phone width (see .table-wrap in index.css) each row turns
// into a stacked card instead: a data-label is copied from the column header
// onto every cell so the CSS can print "COLUMN  value" pairs.
// `scroll` keeps the sideways-scrolling table at every width, for grids that only make
// sense as a matrix (e.g. role × permission).
// `compact` keeps a real table on phones (fewer columns, icon-only buttons) for short
// lists such as the verification queue; `hideOnPhone` names the columns dropped there.
// `subline` names a column that, in the card layout, sits as a small caption under the title
// (a posting's organisation, say).
export function TableWrap({ children, scroll = false, compact = false, hideOnPhone = [], subline }: {
  children: React.ReactNode; scroll?: boolean; compact?: boolean; hideOnPhone?: string[]; subline?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Runs after every render of the parent, so labels follow filtering/sorting.
  useLayoutEffect(() => {
    const table = ref.current?.querySelector("table");
    if (!table) return;
    const heads = Array.from(table.querySelectorAll("thead th")).map((th) => (th.textContent ?? "").trim());
    table.querySelectorAll("thead th").forEach((th, i) => {
      const el = th as HTMLElement;
      if (hideOnPhone.includes(heads[i])) el.dataset.phoneHide = "1"; else delete el.dataset.phoneHide;
    });
    // The row's title is the column that names the thing (Name, Talent, Title…),
    // not necessarily the first one (Verification starts with Email).
    const named = heads.findIndex((h) => /^(name|talent|title|account|user|admin|applicant)$/i.test(h));
    const primaryIndex = named >= 0 ? named : heads.findIndex((h) => h);
    table.querySelectorAll("tbody tr").forEach((tr) => {
      Array.from(tr.children).forEach((cell, i) => {
        const td = cell as HTMLElement;
        const label = heads[i] ?? "";
        if (td.dataset.label !== label) td.dataset.label = label;
        // a cell with nothing in it would print a label over a blank, so hide it
        const blank = !td.querySelector("button, input, img, svg") && /^[—–-]?$/.test((td.textContent ?? "").trim());
        if (blank && label && i !== primaryIndex) td.dataset.empty = "1"; else delete td.dataset.empty;
        // a cell of buttons gets the full card width
        if (td.querySelector("button")) td.dataset.last = "1"; else delete td.dataset.last;
        if (i === primaryIndex) td.dataset.primary = "1"; else delete td.dataset.primary;
        // the status badge sits in the card header, opposite the title
        if (/^status$/i.test(label)) td.dataset.status = "1"; else delete td.dataset.status;
        if (hideOnPhone.includes(label)) td.dataset.phoneHide = "1"; else delete td.dataset.phoneHide;
        if (subline && label === subline) td.dataset.subline = "1"; else delete td.dataset.subline;
      });
    });
  });

  return <div className={["table-wrap", scroll ? "table-scroll" : "", compact ? "table-compact" : ""].filter(Boolean).join(" ")} ref={ref}>{children}</div>;
}
